/**
 * Brand Drawer Component
 *
 * Large drawer with 2 tabs for editing a brand:
 * - Tab 1: General info (name, type, timezone, status)
 * - Tab 2: POS Config (any brand being edited). Carries the Bill uuid used by
 *   the catalogue webhook: company uuid on the enseigne (GROUP), brand uuid on
 *   each restaurant.
 *
 * @see Sprint 20 - Interface Super Admin /bill-admin
 */

import { useState, useEffect } from 'react';
import { Drawer, Button, Input, Select, Checkbox, Tabs, Badge } from '@/components/ui';
import type {
  AdminBrandTreeItem,
  CreateAdminBrandInput,
  UpdateAdminBrandInput,
  UpsertAdminPosConfigInput,
} from '@bstock/shared';
import { useAdminBrand, useAdminPosConfig } from '../hooks/useAdminCompanies';
import {
  useUpdateBrand,
  useCreateBrand,
  useUpsertPosConfig,
  useDeletePosConfig,
} from '../hooks/useAdminMutations';
import { SalesSyncSection } from './SalesSyncSection';
import { BrandSubscriptionsSection } from './BrandSubscriptionsSection';

interface BrandDrawerProps {
  /** Brand ID if editing, null if creating */
  brandId: string | null;
  /** Company ID (required for creating) */
  companyId: string;
  /** Available GROUP brands in the company (for parent selection) */
  availableGroups: AdminBrandTreeItem[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after successful save */
  onSuccess?: () => void;
}

const TIMEZONE_OPTIONS = [
  { value: 'Europe/Paris', label: 'Europe/Paris (France)' },
  { value: 'Europe/Zurich', label: 'Europe/Zurich (Suisse)' },
  { value: 'Africa/Casablanca', label: 'Africa/Casablanca (Maroc)' },
  { value: 'Europe/Brussels', label: 'Europe/Brussels (Belgique)' },
  { value: 'Europe/Luxembourg', label: 'Europe/Luxembourg' },
];

export function BrandDrawer({
  brandId,
  companyId,
  availableGroups,
  open,
  onOpenChange,
  onSuccess,
}: BrandDrawerProps) {
  const isEditing = !!brandId;
  const [activeTab, setActiveTab] = useState('infos');

  // Fetch brand data from core backend (companies, brands info)
  const { data: brand, isLoading: brandLoading } = useAdminBrand(brandId);
  // Fetch POS config separately from bstock backend (pos_configs table
  // lives in the bstock database, not in core)
  const { data: posConfig, isLoading: posConfigLoading } = useAdminPosConfig(
    isEditing ? brandId : null
  );

  // Mutations
  const updateBrandMutation = useUpdateBrand();
  const createBrandMutation = useCreateBrand();
  const upsertPosConfigMutation = useUpsertPosConfig();
  const deletePosConfigMutation = useDeletePosConfig();

  // Form state - Brand Info
  const [name, setName] = useState('');
  const [brandType, setBrandType] = useState<'GROUP' | 'POINT_OF_SALE' | 'CENTRAL'>('POINT_OF_SALE');
  const [parentId, setParentId] = useState<string | undefined>(undefined);
  const [timezone, setTimezone] = useState('Europe/Paris');
  const [isActive, setIsActive] = useState(true);

  // Form state - POS Config
  const [apiKey, setApiKey] = useState('');
  const [autoSync, setAutoSync] = useState(true);
  const [syncInterval, setSyncInterval] = useState('60');
  // Identifiant Bill (sprint webhook-catalogue-bill, US-01) : uuid de la
  // company pour une enseigne, id entier de la brand pour un restaurant.
  // Vide = non relie au webhook.
  const [billRef, setBillRef] = useState('');
  const [billRefError, setBillRefError] = useState<string | undefined>(undefined);

  // Reset form when brand changes
  useEffect(() => {
    if (brand) {
      setName(brand.name);
      setBrandType((brand.type ?? 'POINT_OF_SALE') as 'GROUP' | 'POINT_OF_SALE' | 'CENTRAL');
      setParentId(brand.parentId ?? undefined);
      setTimezone(brand.timezone);
      setIsActive(brand.isActive);

      // POS Config fetched separately from bstock backend
      if (posConfig) {
        setApiKey('');
        setAutoSync(posConfig.autoSync);
        setSyncInterval(posConfig.syncInterval.toString());
        setBillRef(String((brand.type === 'GROUP' ? posConfig.billCompanyId : posConfig.billBrandId) ?? ''));
      } else {
        setApiKey('');
        setAutoSync(true);
        setSyncInterval('60');
        setBillRef('');
      }
    } else {
      // Reset for new brand
      setName('');
      setBrandType('POINT_OF_SALE');
      setParentId(undefined);
      setTimezone('Europe/Paris');
      setIsActive(true);
      setApiKey('');
      setAutoSync(true);
      setSyncInterval('60');
      setBillRef('');
    }
    setActiveTab('infos');
  }, [brand, posConfig, open]);

  // Handle type change (GROUP/POINT_OF_SALE/CENTRAL)
  const handleTypeChange = (newType: 'GROUP' | 'POINT_OF_SALE' | 'CENTRAL') => {
    setBrandType(newType);
    if (newType === 'GROUP') {
      // GROUP cannot have parent
      setParentId(undefined);
    } else if (availableGroups.length > 0 && !parentId) {
      // SITE/CENTRAL needs parent - default to first group
      setParentId(availableGroups[0].id);
    }
  };

  // Save brand info
  const handleSaveBrand = async () => {
    if (isEditing && brandId) {
      const data: UpdateAdminBrandInput = {
        name,
        timezone,
        isActive,
      };
      await updateBrandMutation.mutateAsync({ id: brandId, data });
    } else {
      const data: CreateAdminBrandInput = {
        name,
        type: brandType,
        parentId: brandType !== 'GROUP' ? parentId : undefined,
        timezone,
      };
      await createBrandMutation.mutateAsync({ companyId, data });
    }
    onSuccess?.();
    onOpenChange(false);
  };

  // Save POS config
  const handleSavePosConfig = async () => {
    if (!brandId) return;

    // Restaurant : l'id de brand Bill est un entier. Refuse avant l'envoi —
    // `Number('abc')` vaudrait NaN, serialise en `null`, et effacerait la
    // valeur sans le dire.
    const billRefTrimmed = billRef.trim();
    if (brandType !== 'GROUP' && billRefTrimmed && !/^[1-9]\d*$/.test(billRefTrimmed)) {
      setBillRefError('Id brand Bill : un nombre entier (ex. 3798)');
      return;
    }
    setBillRefError(undefined);

    const data: UpsertAdminPosConfigInput = {
      provider: 'BILL',
      autoSync,
      syncInterval: parseInt(syncInterval, 10),
      // Only include apiKey if provided (for updates, empty means keep existing)
      ...(apiKey.trim() && { apiKey }),
      // Un seul des deux selon le type : l'uuid company n'a de sens que sur
      // l'enseigne, l'id brand que sur un restaurant. Vide = efface.
      ...(brandType === 'GROUP'
        ? { billCompanyId: billRefTrimmed || null }
        : { billBrandId: billRefTrimmed ? Number(billRefTrimmed) : null }),
    };
    await upsertPosConfigMutation.mutateAsync({ brandId, data });
    onSuccess?.();
  };

  // Delete POS config
  const handleDeletePosConfig = async () => {
    if (!brandId) return;
    await deletePosConfigMutation.mutateAsync(brandId);
    setApiKey('');
    onSuccess?.();
  };

  const isLoading = brandLoading || posConfigLoading;
  const isSaving =
    updateBrandMutation.isPending ||
    createBrandMutation.isPending ||
    upsertPosConfigMutation.isPending ||
    deletePosConfigMutation.isPending;

  // Tabs - POS Config visible for all brands when editing
  const showPosConfigTab = isEditing && brand;
  // Modules tab visible only when editing (need a brandId for queries)
  const showModulesTab = isEditing && brandId;

  const tabs = [
    { id: 'infos', label: 'Infos generales' },
    ...(showModulesTab ? [{ id: 'modules', label: 'Modules' }] : []),
    ...(showPosConfigTab ? [{ id: 'pos', label: 'Configuration POS' }] : []),
  ];

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title={isEditing ? `Modifier - ${brand?.name ?? '...'}` : 'Nouveau brand'}
      size="large"
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          {activeTab === 'infos' && (
            <Button
              onClick={handleSaveBrand}
              isLoading={isSaving}
              disabled={!name.trim() || (brandType !== 'GROUP' && !parentId)}
            >
              {isEditing ? 'Enregistrer' : 'Creer'}
            </Button>
          )}
          {activeTab === 'pos' && (
            <Button
              onClick={handleSavePosConfig}
              isLoading={isSaving}
              disabled={!posConfig && !apiKey.trim()}
            >
              Enregistrer POS
            </Button>
          )}
          {activeTab === 'modules' && (
            <Button onClick={() => onOpenChange(false)}>Fermer</Button>
          )}
        </div>
      }
    >
      {isLoading ? (
        <div className="space-y-4 animate-pulse">
          <div className="h-10 bg-neutral-100 rounded-lg" />
          <div className="h-64 bg-neutral-100 rounded-xl" />
        </div>
      ) : (
        <div className="space-y-6">
          <Tabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />

          {/* Tab: Infos generales */}
          {activeTab === 'infos' && (
            <div className="space-y-4">
              <Input
                label="Nom"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Nom du brand"
                required
              />

              {/* Type selection - only for new brands */}
              {!isEditing && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-neutral-700">Type</label>
                  <div className="flex flex-col gap-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="brandType"
                        checked={brandType === 'GROUP'}
                        onChange={() => handleTypeChange('GROUP')}
                        className="w-4 h-4 text-primary-600"
                      />
                      <span className="text-sm">GROUP (organisationnel, sans stock physique)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="brandType"
                        checked={brandType === 'POINT_OF_SALE'}
                        onChange={() => handleTypeChange('POINT_OF_SALE')}
                        className="w-4 h-4 text-primary-600"
                      />
                      <span className="text-sm">SITE (restaurant physique)</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="brandType"
                        checked={brandType === 'CENTRAL'}
                        onChange={() => handleTypeChange('CENTRAL')}
                        className="w-4 h-4 text-primary-600"
                      />
                      <span className="text-sm">CENTRAL (centrale d'achat / entrepot)</span>
                    </label>
                  </div>
                </div>
              )}

              {/* Show type badge when editing */}
              {isEditing && brand && (
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-neutral-700">Type:</span>
                  <Badge variant={brand.type === 'GROUP' ? 'info' : brand.type === 'CENTRAL' ? 'warning' : 'primary'}>
                    {brand.type}
                  </Badge>
                </div>
              )}

              {/* Parent selection - only for SITE/CENTRAL brands */}
              {brandType !== 'GROUP' && !isEditing && (
                <Select
                  label="Parent (GROUP)"
                  value={parentId ?? ''}
                  onChange={(value) => setParentId(value || undefined)}
                  options={availableGroups.map((g) => ({ value: g.id, label: g.name }))}
                />
              )}

              {/* Show parent when editing SITE */}
              {isEditing && brand?.parentId && (
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-neutral-700">Parent:</span>
                  <span className="text-sm text-neutral-600">
                    {availableGroups.find((g) => g.id === brand.parentId)?.name ?? brand.parentId}
                  </span>
                </div>
              )}

              <Select
                label="Timezone"
                value={timezone}
                onChange={(value) => setTimezone(value)}
                options={TIMEZONE_OPTIONS}
              />

              {isEditing && (
                <div className="pt-2">
                  <Checkbox
                    label="Brand actif"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                  />
                </div>
              )}
            </div>
          )}

          {/* Tab: Modules (souscriptions par produit) */}
          {activeTab === 'modules' && showModulesTab && brandId && brand && (
            <BrandSubscriptionsSection brandId={brandId} brandType={brand.type} />
          )}

          {/* Tab: Configuration POS */}
          {activeTab === 'pos' && showPosConfigTab && (
            <div className="space-y-4">
              {/* Status display */}
              {posConfig && (
                <div className="p-4 bg-neutral-50 rounded-lg border border-neutral-200 space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">Dernier sync:</span>
                    <span className="text-sm text-neutral-600">
                      {posConfig.lastSyncAt
                        ? new Date(posConfig.lastSyncAt).toLocaleString('fr-FR')
                        : 'Jamais'}
                    </span>
                  </div>
                  {posConfig.lastSyncStatus && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">Statut:</span>
                      <Badge
                        variant={
                          posConfig.lastSyncStatus === 'SUCCESS'
                            ? 'success'
                            : posConfig.lastSyncStatus === 'ERROR'
                              ? 'danger'
                              : 'warning'
                        }
                      >
                        {posConfig.lastSyncStatus}
                      </Badge>
                    </div>
                  )}
                  {posConfig.lastSyncError && (
                    <div className="text-sm text-danger-600 mt-2">
                      Erreur: {posConfig.lastSyncError}
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm text-neutral-500">
                    <span>API Key actuelle:</span>
                    <code className="px-2 py-0.5 bg-neutral-200 rounded text-xs">
                      {posConfig.apiKey}
                    </code>
                  </div>
                </div>
              )}

              <Select
                label="Provider"
                value="BILL"
                onChange={() => {}}
                options={[{ value: 'BILL', label: 'BILL' }]}
                disabled
              />

              <Input
                label="API Key"
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={
                  posConfig ? "Laisser vide pour conserver l'ancienne cle" : 'Entrez la cle API'
                }
                helperText={posConfig ? 'Entrez une nouvelle cle pour la remplacer' : undefined}
              />

              <Input
                label={brandType === 'GROUP' ? 'Uuid company Bill' : 'Id brand Bill'}
                value={billRef}
                onChange={(e) => {
                  setBillRef(e.target.value);
                  setBillRefError(undefined);
                }}
                inputMode={brandType === 'GROUP' ? undefined : 'numeric'}
                placeholder={brandType === 'GROUP' ? 'ex. 9c41d2a0-1b2c-4d5e-8f90-123456789abc' : 'ex. 3798'}
                error={billRefError}
                helperText={
                  brandType === 'GROUP'
                    ? "Identifiant de l'enseigne chez Bill. Sert au webhook catalogue (prix et ruptures en direct). Vide = pas de webhook, synchronisation de nuit seulement."
                    : 'Identifiant de ce restaurant chez Bill. Sert au webhook catalogue (prix et ruptures en direct). Vide = pas de webhook, synchronisation de nuit seulement.'
                }
              />

              <div className="pt-2">
                <Checkbox
                  label="Synchronisation automatique"
                  checked={autoSync}
                  onChange={(e) => setAutoSync(e.target.checked)}
                />
              </div>

              <Input
                label="Intervalle de sync (minutes)"
                type="number"
                value={syncInterval}
                onChange={(e) => setSyncInterval(e.target.value)}
                min={10}
                max={600}
                helperText="Entre 10 et 600 minutes (10h max)"
              />

              {/* Sales Sync Section - visible when POS config exists */}
              {posConfig && brandId && (
                <div className="pt-4 border-t border-neutral-200">
                  <SalesSyncSection brandId={brandId} />
                </div>
              )}

              {/* Delete POS config button */}
              {posConfig && (
                <div className="pt-4 border-t border-neutral-200">
                  <Button
                    variant="danger"
                    onClick={handleDeletePosConfig}
                    isLoading={deletePosConfigMutation.isPending}
                  >
                    Supprimer la configuration POS
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
}
